import React, { useState, useEffect, useCallback } from "react";
import {
  BookOpen, Download, Sparkles, Loader2, Plus, Trash2, Check, X, Shield, Search, Users, Edit2
} from "lucide-react";
import PolicyVersionManager from "./PolicyVersionManager";
import EmployeeVoiceCard from "./EmployeeVoiceCard";
import ArchiveAndDuplicatesPanel from "./ArchiveAndDuplicatesPanel";
import ReportingHierarchyCard from "./ReportingHierarchyCard";
import AttendancePanel from "./AttendancePanel";
import PayrollPanel from "./PayrollPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only Team Hub extras — stacked card group:
 *   1. Super-Admin Training Manual (download + info)
 *   2. AI Process Guide Generator (DoozyBot writes step-by-step playbooks on demand)
 *   3. Designations Manager (built-in + custom roles)
 *   4. Reporting Hierarchy (HR / Admin)
 *   5. Attendance (HR / Admin)
 *   6. Payroll (HR review + Finance process)
 */
const TeamHubExtras = ({ authAxios }) => {
  return (
    <div className="space-y-4" data-testid="team-hub-extras">
      <div className="grid md:grid-cols-2 gap-4">
        <TrainingManualCard authAxios={authAxios} />
        <AIProcessGuideCard authAxios={authAxios} />
      </div>
      <DesignationsManager authAxios={authAxios} />
      <ReportingHierarchyCard authAxios={authAxios} />
      <AttendancePanel authAxios={authAxios} />
      <PayrollPanel authAxios={authAxios} />
      <ArchiveAndDuplicatesPanel authAxios={authAxios} />
      <EmployeeVoiceCard authAxios={authAxios} />
      <PolicyVersionManager authAxios={authAxios} />
    </div>
  );
};

// ═════════════════════════════════════════════════════════
// 1. Training Manual card
// ═════════════════════════════════════════════════════════
const TrainingManualCard = ({ authAxios }) => {
  const [info, setInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    authAxios
      .get(`${API_URL}/api/admin/training-manual/info`)
      .then((r) => setInfo(r.data))
      .catch(() => {});
  }, [authAxios]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/training-manual/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "DoozyGo_Super_Admin_Training_Manual.md";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Download failed. Please retry in a minute.");
    }
    setDownloading(false);
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="training-manual-card">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] flex items-center justify-center flex-shrink-0">
          <BookOpen size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-[#1E1B4B] text-sm">Super Admin Training Manual</h3>
          <p className="text-xs text-[#94a3b8] mt-0.5">
            Complete step-by-step website guide — onboarding, approvals, bookings, AI, refunds, security.
          </p>
          {info?.size_bytes > 0 && (
            <p className="text-[10px] text-[#94a3b8] mt-1 font-mono">
              {(info.size_bytes / 1024).toFixed(1)} KB · stored securely in HR folder
            </p>
          )}
          <button
            onClick={download}
            disabled={downloading}
            className="mt-3 flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E83E8C] text-white text-xs font-bold hover:bg-[#c22d78] disabled:opacity-50"
            data-testid="download-manual-btn"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {downloading ? "Downloading…" : "Download Manual"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════
// 2. AI Process Guide generator
// ═════════════════════════════════════════════════════════
const AIProcessGuideCard = ({ authAxios }) => {
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("staff");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!topic.trim() || generating) return;
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await authAxios.post(`${API_URL}/api/admin/training-manual/generate-guide`, {
        topic: topic.trim(),
        audience,
      });
      setResult(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to generate — retry in a minute.");
    }
    setGenerating(false);
  };

  const downloadResult = () => {
    if (!result) return;
    const blob = new Blob([result.content_md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${result.guide_id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="ai-guide-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#E83E8C] flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-[#1E1B4B] text-sm">AI Process Guide Generator</h3>
          <p className="text-xs text-[#94a3b8] mt-0.5">
            Type any process. DoozyBot writes rules + step-by-step playbook, saved to HR folder.
          </p>
        </div>
      </div>
      <input
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && generate()}
        placeholder='e.g. "How to onboard a new decorator"'
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C]/50"
        data-testid="ai-guide-topic"
      />
      <div className="flex gap-2 mt-2">
        <select
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg flex-1"
          data-testid="ai-guide-audience"
        >
          <option value="staff">Staff (everyone)</option>
          <option value="ops_manager">Ops Manager</option>
          <option value="decorator">Decorator</option>
          <option value="sales_manager">Sales Manager</option>
          <option value="sales_executive">Sales Executive</option>
          <option value="cfo">CFO / Finance</option>
          <option value="security_manager">Security</option>
        </select>
        <button
          onClick={generate}
          disabled={!topic.trim() || generating}
          className="px-4 py-1.5 rounded-lg bg-[#1E1B4B] text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5"
          data-testid="ai-guide-generate"
        >
          {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {generating ? "Writing…" : "Generate"}
        </button>
      </div>
      {result && (
        <div className="mt-3 border border-gray-200 rounded-xl p-3 bg-gray-50 max-h-60 overflow-y-auto" data-testid="ai-guide-result">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] text-[#94a3b8] font-mono">{result.guide_id}</p>
            <button
              onClick={downloadResult}
              className="text-[11px] text-[#E83E8C] font-bold hover:underline flex items-center gap-1"
              data-testid="ai-guide-download"
            >
              <Download size={11} /> Download .md
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-[11px] text-gray-700 font-mono leading-relaxed">
            {result.content_md}
          </pre>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════
// 3. Designations Manager
// ═════════════════════════════════════════════════════════
const DesignationsManager = ({ authAxios }) => {
  const [data, setData] = useState({ designations: [], available_tabs: [] });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ key: "", label: "", level: 5, description: "", tabs: [], headcount_target: "", reports_to: "" });
  const [search, setSearch] = useState("");
  const [editTargetKey, setEditTargetKey] = useState(null);
  const [editTargetValue, setEditTargetValue] = useState("");
  const [editReportsKey, setEditReportsKey] = useState(null);
  const [editReportsValue, setEditReportsValue] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/designations`);
      setData(r.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleTab = (t) => {
    setForm((f) => ({
      ...f,
      tabs: f.tabs.includes(t) ? f.tabs.filter((x) => x !== t) : [...f.tabs, t],
    }));
  };

  const submit = async () => {
    if (!form.key || !form.label || form.tabs.length === 0) {
      alert("Key, label and at least one tab are required.");
      return;
    }
    try {
      await authAxios.post(`${API_URL}/api/admin/designations`, {
        ...form,
        headcount_target: form.headcount_target === "" ? null : Number(form.headcount_target),
        reports_to: form.reports_to || null,
      });
      setShowNew(false);
      setForm({ key: "", label: "", level: 5, description: "", tabs: [], headcount_target: "", reports_to: "" });
      fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create designation");
    }
  };

  const saveReportsTo = async (key) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/designations/${key}/reports-to`, {
        reports_to: editReportsValue || null,
      });
      setEditReportsKey(null);
      setEditReportsValue("");
      fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update reports_to");
    }
  };

  const saveTarget = async (key) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/designations/${key}/headcount`, {
        headcount_target: editTargetValue === "" ? null : Number(editTargetValue),
      });
      setEditTargetKey(null);
      setEditTargetValue("");
      fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update target");
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete designation "${key}"?`)) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/designations/${key}`);
      fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const filtered = data.designations.filter(
    (d) =>
      !search.trim() ||
      d.label.toLowerCase().includes(search.toLowerCase()) ||
      d.key.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="designations-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#10B981] to-[#1E1B4B] flex items-center justify-center">
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm">Designations (Roles) & Access</h3>
            <p className="text-xs text-[#94a3b8]">
              {data.designations.length} designations · pick any of {data.available_tabs.length} tabs
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#E83E8C] text-white text-xs font-bold hover:bg-[#c22d78]"
          data-testid="new-designation-btn"
        >
          <Plus size={14} /> New Designation
        </button>
      </div>

      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search designations…"
          className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl outline-none focus:border-[#E83E8C]/50"
        />
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {filtered.map((d) => (
            <div
              key={d.key}
              className="flex items-start justify-between border border-gray-100 rounded-xl p-3 hover:border-[#E83E8C]/30 transition"
              data-testid={`designation-${d.key}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm text-[#1E1B4B]">{d.label}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">{d.key}</span>
                  {d.builtin && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">built-in</span>
                  )}
                  {!d.builtin && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-semibold">custom</span>
                  )}
                </div>
                {d.description && <p className="text-[11px] text-gray-500 mt-0.5">{d.description}</p>}
                {/* Headcount */}
                <div className="flex items-center gap-2 mt-1.5" data-testid={`headcount-${d.key}`}>
                  <Users size={11} className="text-gray-400" />
                  <span className="text-[11px] font-semibold text-[#1E1B4B]">
                    {d.headcount_current || 0}
                    {d.headcount_target != null && d.headcount_target !== undefined ? (
                      <>
                        <span className="text-gray-400"> / </span>
                        <span className={d.headcount_current >= d.headcount_target ? "text-green-600" : "text-amber-600"}>
                          {d.headcount_target}
                        </span>
                      </>
                    ) : null}
                  </span>
                  {d.headcount_target != null && d.headcount_target > 0 && (
                    <div className="flex-1 max-w-[140px] h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${d.headcount_current >= d.headcount_target ? "bg-green-500" : "bg-gradient-to-r from-[#F59E0B] to-[#E83E8C]"}`}
                        style={{ width: `${Math.min(100, Math.round((d.headcount_current / Math.max(d.headcount_target, 1)) * 100))}%` }}
                      />
                    </div>
                  )}
                  {d.headcount_target != null && d.headcount_target > d.headcount_current && (
                    <span className="text-[10px] font-semibold text-amber-600">
                      need {d.headcount_target - d.headcount_current} more
                    </span>
                  )}
                  {/* Edit target — custom roles only */}
                  {!d.builtin && editTargetKey !== d.key && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditTargetKey(d.key); setEditTargetValue(d.headcount_target ?? ""); }}
                      className="ml-auto text-[10px] text-gray-400 hover:text-[#E83E8C] flex items-center gap-0.5"
                      data-testid={`edit-target-${d.key}`}
                    >
                      <Edit2 size={10} /> {d.headcount_target == null ? "set target" : "edit"}
                    </button>
                  )}
                  {!d.builtin && editTargetKey === d.key && (
                    <div className="ml-auto flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        value={editTargetValue}
                        onChange={(e) => setEditTargetValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="0"
                        className="w-14 px-2 py-0.5 text-[11px] border border-gray-200 rounded"
                        data-testid={`target-input-${d.key}`}
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); saveTarget(d.key); }}
                        className="p-1 rounded bg-[#E83E8C] text-white"
                        data-testid={`save-target-${d.key}`}
                      >
                        <Check size={10} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditTargetKey(null); }}
                        className="p-1 rounded bg-gray-100 text-gray-500"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {d.tabs.map((t) => (
                    <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-[#FFF0F6] text-[#E83E8C] font-semibold">
                      {t}
                    </span>
                  ))}
                </div>
                {/* Reports to — custom roles only (built-in is authoritative and lives in code) */}
                {!d.builtin && (
                  <div className="flex items-center gap-2 mt-1.5 text-[11px]">
                    <span className="text-gray-500">Reports to:</span>
                    {editReportsKey === d.key ? (
                      <>
                        <select
                          value={editReportsValue}
                          onChange={(e) => setEditReportsValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[11px] px-2 py-0.5 border border-gray-200 rounded"
                          data-testid={`reports-to-select-${d.key}`}
                        >
                          <option value="">— none —</option>
                          {data.designations.filter(x => x.key !== d.key).map((opt) => (
                            <option key={opt.key} value={opt.key}>{opt.label} · {opt.key}</option>
                          ))}
                        </select>
                        <button
                          onClick={(e) => { e.stopPropagation(); saveReportsTo(d.key); }}
                          className="p-1 rounded bg-[#E83E8C] text-white"
                          data-testid={`save-reports-to-${d.key}`}
                        ><Check size={10} /></button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditReportsKey(null); }}
                          className="p-1 rounded bg-gray-100 text-gray-500"
                        ><X size={10} /></button>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-[#1E1B4B]">
                          {d.reports_to ? <span className="font-mono">{d.reports_to}</span> : <span className="text-gray-400">— none —</span>}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditReportsKey(d.key); setEditReportsValue(d.reports_to || ""); }}
                          className="text-gray-400 hover:text-[#E83E8C] flex items-center gap-0.5"
                          data-testid={`edit-reports-to-${d.key}`}
                        >
                          <Edit2 size={10} /> edit
                        </button>
                      </>
                    )}
                  </div>
                )}
                {d.builtin && d.reports_to && (
                  <div className="flex items-center gap-2 mt-1.5 text-[11px]">
                    <span className="text-gray-500">Reports to:</span>
                    <span className="font-mono font-semibold text-[#1E1B4B]">{d.reports_to}</span>
                  </div>
                )}
              </div>
              {!d.builtin && (
                <button
                  onClick={() => remove(d.key)}
                  className="ml-2 p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                  data-testid={`delete-designation-${d.key}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-designation-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#1E1B4B]">New Designation</h3>
              <button onClick={() => setShowNew(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="space-y-2">
              <input
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                placeholder="Key (e.g. regional_head)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl"
                data-testid="new-designation-key"
              />
              <input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Label (e.g. Regional Head)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl"
                data-testid="new-designation-label"
              />
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description (optional)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl"
              />
              <div>
                <label className="text-[11px] font-semibold text-[#1E1B4B] mb-1 block">
                  Target headcount (optional — how many of this role do we need?)
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.headcount_target}
                  onChange={(e) => setForm({ ...form, headcount_target: e.target.value })}
                  placeholder="e.g. 3"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl"
                  data-testid="new-designation-target"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Shows a progress bar in the list. Leave blank if the team size is open-ended.
                </p>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#1E1B4B] mb-1 block">
                  Reports to (optional — choose the designation above this one in the org chart)
                </label>
                <select
                  value={form.reports_to}
                  onChange={(e) => setForm({ ...form, reports_to: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl"
                  data-testid="new-designation-reports-to"
                >
                  <option value="">— none (top-level) —</option>
                  {data.designations.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label} · {opt.key}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  When you add staff with this designation, we'll suggest managers with the "{form.reports_to || "selected"}" role automatically.
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#1E1B4B] mb-1.5">Tabs this role can access:</p>
                <div className="grid grid-cols-2 gap-1.5 p-2 border border-gray-100 rounded-xl max-h-52 overflow-y-auto">
                  {data.available_tabs.map((t) => (
                    <label key={t} className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.tabs.includes(t)}
                        onChange={() => toggleTab(t)}
                        className="accent-[#E83E8C]"
                        data-testid={`tab-checkbox-${t}`}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowNew(false)} className="px-4 py-2 text-xs rounded-xl border border-gray-200">Cancel</button>
                <button
                  onClick={submit}
                  className="px-4 py-2 text-xs rounded-xl bg-[#E83E8C] text-white font-bold flex items-center gap-1"
                  data-testid="submit-designation"
                >
                  <Check size={12} /> Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamHubExtras;
